import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Label, Switch, ToggleGroup, ToggleGroupItem } from '@monksflow/monks-ui';
import { TriangleAlert } from 'lucide-react';
import type { TaxonomyListColumn, TaxonomyListRow } from '@/entities/taxonomy-list';
import { FormSheet } from '@/shared/ui/FormSheet';
import { looksLikeHeader, parseTabular } from '../../lib/parseTabular';

export interface BulkPasteModalProps {
  open: boolean;
  columns: TaxonomyListColumn[];
  /** The columns' stored names, when `columns` carries display labels: a header row may use either. */
  storedNames?: string[];
  existingRowCount: number;
  /** False hides "Replace all", for a list whose other columns a replace would wipe. */
  allowReplace?: boolean;
  onClose: () => void;
  /** Applies the parsed rows, either replacing all rows or appending them. */
  onApply: (rows: TaxonomyListRow[], mode: 'replace' | 'append') => void;
}

/**
 * Bulk entry for vocabularies — the SME "paste from the spreadsheet" path.
 *
 * A textarea rather than a hidden clipboard handler because it works with an
 * empty list, is discoverable, and lets the user see and correct the parse
 * before committing. Accepts tab-separated (Excel/Sheets) or comma-separated
 * text and previews exactly what will be created.
 */
export function BulkPasteModal({
  open,
  columns,
  storedNames,
  existingRowCount,
  allowReplace = true,
  onClose,
  onApply,
}: BulkPasteModalProps) {
  const { t } = useTranslation('knowledge');
  const [text, setText] = useState('');
  const [hasHeader, setHasHeader] = useState(false);
  const [mode, setMode] = useState<'replace' | 'append'>('append');
  const [touchedHeader, setTouchedHeader] = useState(false);

  useEffect(() => {
    if (!open) {
      setText('');
      setHasHeader(false);
      setMode('append');
      setTouchedHeader(false);
    }
  }, [open]);

  const grid = useMemo(() => parseTabular(text), [text]);

  // Pre-tick "first row is a header" when it looks like one, until the user
  // decides for themselves.
  const columnNames = useMemo(() => columns.map((c) => c.name), [columns]);

  useEffect(() => {
    if (touchedHeader) return;
    setHasHeader(
      looksLikeHeader(grid, columnNames) ||
        (storedNames !== undefined && looksLikeHeader(grid, storedNames)),
    );
  }, [grid, columnNames, storedNames, touchedHeader]);

  const dataRows = hasHeader ? grid.slice(1) : grid;
  const widest = dataRows.reduce((m, r) => Math.max(m, r.length), 0);
  const extraColumns = Math.max(0, widest - columns.length);

  const preview = dataRows.slice(0, 5);

  const handleApply = () => {
    if (dataRows.length === 0) return;
    const rows: TaxonomyListRow[] = dataRows.map((cells) => ({
      id: crypto.randomUUID(),
      cells: Object.fromEntries(
        columns
          .map((col, i): [string, string] => [col.id, (cells[i] ?? '').trim()])
          .filter(([, v]) => v !== ''),
      ),
    }));
    onApply(rows, mode);
    onClose();
  };

  return (
    <FormSheet
      className="w-[44rem]"
      open={open}
      onOpenChange={(next) => !next && onClose()}
      title={t('taxonomy.bulkPasteModal.pasteOptionsSpreadsheet')}
    >
      <FormSheet.Body className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
        <p className="text-xs text-muted-foreground">
          {t('taxonomy.bulkPasteModal.copyCellsStraightExcelGoogle')}{' '}
          <span className="font-medium text-foreground">
            {columns.map((c) => c.name).join(' · ')}
          </span>
          .
        </p>

        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={8}
          // eslint-disable-next-line jsx-a11y/no-autofocus -- first field of a modal the user just opened to type into
          autoFocus
          placeholder={t('taxonomy.bulkPasteModal.northAmericaTnaNemeaTem')}
          className="w-full resize-y rounded-md border border-border-light bg-input px-3 py-2 font-mono text-xs text-foreground outline-none focus:border-primary"
          aria-label={t('taxonomy.bulkPasteModal.pasteSpreadsheetData')}
        />

        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <Switch
              checked={hasHeader}
              onCheckedChange={(v) => {
                setTouchedHeader(true);
                setHasHeader(v);
              }}
              aria-label={t('taxonomy.bulkPasteModal.firstRowHeader')}
            />
            <Label className="text-sm font-normal">
              {t('taxonomy.bulkPasteModal.firstRowHeader')}
            </Label>
          </div>

          {allowReplace && (
            <ToggleGroup
              aria-label={t('taxonomy.bulkPasteModal.pasteMode')}
              type="single"
              value={mode}
              onValueChange={(v) => v && setMode(v as 'replace' | 'append')}
              variant="outline"
              size="sm"
            >
              {/* eslint-disable-next-line i18next/no-literal-string -- paste-mode enum */}
              <ToggleGroupItem value="append" className="px-3">
                {t('taxonomy.bulkPasteModal.addToExisting', { count: existingRowCount })}
              </ToggleGroupItem>
              {/* eslint-disable-next-line i18next/no-literal-string -- paste-mode enum */}
              <ToggleGroupItem value="replace" className="px-3">
                {t('taxonomy.bulkPasteModal.replaceAll')}
              </ToggleGroupItem>
            </ToggleGroup>
          )}
        </div>

        {extraColumns > 0 && (
          <p className="flex items-start gap-1.5 rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-700 dark:bg-amber-500/10 dark:text-amber-300">
            <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
            {t('taxonomy.bulkPasteModal.extraColumnsIgnored', {
              widest,
              count: extraColumns,
            })}
          </p>
        )}

        {dataRows.length > 0 && (
          <div className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t('taxonomy.bulkPasteModal.previewRows', { count: dataRows.length })}
            </span>
            <div className="overflow-auto rounded-md border border-border-light">
              <table className="w-full border-separate border-spacing-0 text-xs">
                <thead>
                  <tr>
                    {columns.map((c) => (
                      <th
                        key={c.id}
                        className="border-b border-l border-border-light bg-surface px-2 py-1 text-left font-semibold first:border-l-0"
                      >
                        {c.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.map((cells, i) => (
                    <tr key={i}>
                      {columns.map((c, ci) => (
                        <td
                          key={c.id}
                          className="truncate border-b border-l border-border-light px-2 py-1 first:border-l-0"
                        >
                          {cells[ci] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {dataRows.length > preview.length && (
              <span className="text-xs text-muted-foreground">
                {t('taxonomy.bulkPasteModal.and')} {dataRows.length - preview.length}{' '}
                {t('taxonomy.bulkPasteModal.more')}
              </span>
            )}
          </div>
        )}
      </FormSheet.Body>

      <FormSheet.Footer
        primary={{
          label:
            dataRows.length > 0
              ? mode === 'replace'
                ? t('taxonomy.bulkPasteModal.replaceRows', { count: dataRows.length })
                : t('taxonomy.bulkPasteModal.addRowCount', { count: dataRows.length })
              : t('taxonomy.bulkPasteModal.addRows'),
          onClick: handleApply,
          disabled: dataRows.length === 0,
        }}
        secondary={{ label: t('taxonomy.bulkPasteModal.cancel'), onClick: onClose }}
      />
    </FormSheet>
  );
}

BulkPasteModal.displayName = 'BulkPasteModal';
